import { from, Observable, of } from 'rxjs'
import { catchError, first, map, switchMap } from 'rxjs/operators'

import { SearchResult, SearchFeatured, Wemi, getTranslatedString, ThesaurusTerm } from '@skinsoft/types'
import { preferredTerm } from '@skinsoft/selectors/thesaurus-term-selectors'
import { wemiSelectors } from '@skinsoft/selectors'
import { isWemi } from '@skinsoft/wemi-utils'
import { resolveAuthorFunction, resolveDenomination, resolveDomain, resolveInstitution, resolveLastReportDate } from '@skinsoft/entity-ui/src/item/serializer/resolve-fields'
import { searchResultSelectors } from '..'
import { ThesaurusTermDataStore } from '@skinsoft/entity'


export function toSearchFeatured$(itemSearchResult: SearchResult | Wemi, locale: string, fromSearch: boolean = true): Observable<SearchFeatured> {

    let searchFeatured: SearchFeatured = null as any;
    if (isWemi(itemSearchResult)) {
        searchFeatured = {
            creation_date: wemiSelectors.creationDate(itemSearchResult as Wemi),
            credits: wemiSelectors.credits(itemSearchResult as Wemi), // author name and function
            denomination: wemiSelectors.name(itemSearchResult as Wemi),  // ex  : sculpture, painting etc ...
            domain: wemiSelectors.domain(itemSearchResult as Wemi),  // ex  : sculpture, painting etc ...
            expressionId: wemiSelectors.expressionId(itemSearchResult as Wemi),
            institution: wemiSelectors.institution(itemSearchResult as Wemi),
            inventoryNumber: wemiSelectors.inventoryNumber(itemSearchResult as Wemi, locale),
            itemId: wemiSelectors.id(itemSearchResult as Wemi),
            itemType: wemiSelectors.itemType(itemSearchResult as Wemi),
            lastConditionReportId: wemiSelectors.lastConditionReport(itemSearchResult as Wemi),
            lastDateOfConditionReport: wemiSelectors.lastDateOfConditionReport(itemSearchResult as Wemi),
            manifestationId: wemiSelectors.manifestationId(itemSearchResult as Wemi),
            materialAndTechniques: summarizeMaterialsAndTechniques(wemiSelectors.materialsAndTechnics(itemSearchResult as Wemi), locale),
            measurements: summarizeMeasurements(wemiSelectors.measurements(itemSearchResult as Wemi), locale),
            media: wemiSelectors.media(itemSearchResult as Wemi),
            title: wemiSelectors.title(itemSearchResult as Wemi, locale),
            weight: wemiSelectors.weight(itemSearchResult as Wemi),
            workId: wemiSelectors.workId(itemSearchResult as Wemi),
        };
    } else {
        searchFeatured = {
            creation_date: searchResultSelectors.creationDate(itemSearchResult as SearchResult),
            credits: searchResultSelectors.credits(itemSearchResult as SearchResult),
            denomination: searchResultSelectors.denomination(itemSearchResult as SearchResult),
            domain: searchResultSelectors.domain(itemSearchResult as SearchResult),
            expressionId: "",
            institution: searchResultSelectors.institution(itemSearchResult as SearchResult),
            inventoryNumber: searchResultSelectors.inventoryNumber(itemSearchResult as SearchResult, locale),
            itemId: searchResultSelectors.id(itemSearchResult as SearchResult),
            itemType: searchResultSelectors.itemType(itemSearchResult as SearchResult) ?? '',
            lastConditionReportId: searchResultSelectors.lastConditionReport(itemSearchResult as SearchResult),
            // @ts-ignore
            lastDateOfConditionReport: searchResultSelectors.lastDateOfConditionReport(itemSearchResult as SearchResult),
            manifestationId: "",
            materialAndTechniques: searchResultSelectors.materialsAndTechnics(itemSearchResult as SearchResult, locale),
            measurements: searchResultSelectors.measurements(itemSearchResult as SearchResult, locale),
            media: searchResultSelectors.media(itemSearchResult as SearchResult),
            title: searchResultSelectors.title(itemSearchResult as SearchResult, locale),
            weight: searchResultSelectors.weight(itemSearchResult as SearchResult),
            workId: ""
        };
    }


    const featured$ = of(searchFeatured);

    return featured$
        .pipe(
            switchMap(
                async theSearchFeatured => {
                    await Promise.all([
                        resolveInstitution(theSearchFeatured, locale).then(result => theSearchFeatured.institution = result),
                        resolveAuthorFunction(theSearchFeatured, locale).then(result => theSearchFeatured.credits.authorFunction = result),
                        resolveDomain(theSearchFeatured, locale).then(result => theSearchFeatured.domain = result),
                        resolveDenomination(theSearchFeatured, locale).then(result => theSearchFeatured.denomination = result),
                        resolveLastReportDate(theSearchFeatured, locale).then(result => theSearchFeatured.lastDateOfConditionReport = result),
                    ])

                    return theSearchFeatured;
                }
            ),
            // Resolve measurements
            switchMap(
                theSearchFeatured => {
                    return from(theSearchFeatured.measurements).pipe(
                        map(measurements => {
                            return {
                                ...theSearchFeatured,
                                measurements: measurements || []
                            }
                        })
                    )
                }
            ),
            // Resolve materials and techniques
            switchMap(
                theSearchFeatured => {
                    return from(theSearchFeatured.materialAndTechniques).pipe(
                        map(mAndT => {
                            return {
                                ...theSearchFeatured,
                                materialAndTechniques: mAndT || []
                            }
                        })
                    )
                }
            ),
            catchError(error => featured$))
}

async function summarizeMaterialsAndTechniques(matAndTechs, locale: string): Promise<string[]> {
    const resolvedMAndT: string[] = [];
    if (matAndTechs) {
        for (const mt of matAndTechs) {
            const materials = mt.materials;
            const techniques = mt.techniques;

            let materialsSummary: string = "";
            let matIndex = 0;
            // Looping through materials
            if (materials) {
                for (const mat of materials) {
                    // material
                    let matTitle = null
                    if (mat.title && typeof mat.title === 'string') {
                        const matTerm = await ThesaurusTermDataStore.getInstance().fetchOne(mat.material).pipe(first()).toPromise()
                        matTitle = getTranslatedString(preferredTerm(matTerm as ThesaurusTerm), locale)
                    }

                    // color
                    let colorTitle = null;
                    if (mat.color && typeof mat.color === 'string') {
                        const colorTerm = await ThesaurusTermDataStore.getInstance().fetchOne(mat.color).pipe(first()).toPromise()
                        colorTitle = getTranslatedString(preferredTerm(colorTerm as ThesaurusTerm), locale)
                    }

                    // precision
                    let precision = null;
                    if (mat.precision) {
                        precision = getTranslatedString(mat.precision, locale)
                    }

                    const matSummary: string = matTitle ? matTitle : ''
                        + (colorTitle ? ` ${colorTitle}` : "")
                        + (precision ? ` (${precision})` : "")

                    if (matIndex > 0) {
                        materialsSummary += ", "
                    }

                    materialsSummary += matSummary;
                    matIndex++;
                }
            }
            let technicsSummary: string = "";
            let techIndex = 0;
            if (techniques) {
                for (const tech of techniques) {
                    // technique
                    let techTitle = null
                    if (tech.title || typeof tech.title === 'string') {
                        const techTerm = await ThesaurusTermDataStore.getInstance().fetchOne(tech.technique).pipe(first()).toPromise()
                        techTitle = getTranslatedString(preferredTerm(techTerm as ThesaurusTerm), locale)
                    }

                    // precision
                    let precision = null;
                    if (tech.precision) {
                        precision = getTranslatedString(tech.precision, locale)
                    }

                    const techSummary: string = techTitle
                        + (precision ? ` (${precision})` : "")

                    if (techIndex > 0) {
                        technicsSummary += ", "
                    }
                    technicsSummary += techSummary
                    techIndex++;
                }
            }
            resolvedMAndT.push(materialsSummary + (technicsSummary ? ` / ${technicsSummary}` : ""))
        }
    }
    return resolvedMAndT;
}

async function summarizeMeasurements(measures, locale: string): Promise<string[]> {
    const measuresSummary: string[] = []
    if (measures) {
        for (const measurementGroup of measures) {
            if (measurementGroup && measurementGroup.measures) {
                for (const measure of measurementGroup.measures) {
                    let measureType: string = null as any;
                    let measureValue: string = null as any;
                    let measureUnit: string = null as any;
                    if (measure.typeOfMeasure) {
                        const term = await ThesaurusTermDataStore.getInstance().fetchOne(measure.typeOfMeasure).pipe(first()).toPromise()
                        measureType = getTranslatedString(preferredTerm(term), locale)
                    }
                    if (measure.value) {
                        measureValue = getTranslatedString(measure.value, locale) as string
                    }
                    if (measure.unit) {
                        const term = await ThesaurusTermDataStore.getInstance().fetchOne(measure.unit).pipe(first()).toPromise()
                        measureUnit = getTranslatedString(preferredTerm(term), locale)
                    }
                    if (measureValue) {
                        const measureSummary =
                            (measureType ? `${measureType} : ` : "")
                            + measureValue
                            + (measureUnit ? ` ${measureUnit}` : "")

                        measuresSummary.push(measureSummary);
                    }
                }
            }
        }
    }
    return measuresSummary;
}
